// src/pages/driverReports/carSheets/CarExpenses/CarExpenses.js

import React from "react";
import { useCarSheet } from "./useCarSheet";
import { db } from "../../../../firebase/firebase";
import { doc, updateDoc } from "firebase/firestore";

const EDITABLE_INCOME_LABELS = new Set([
  "OTHER ADJUSTMENT",
  "PAYMENT RECEIVED",
  "CASH RECEIVED / PAID TO DRIVER", // ⭐ ADD THIS LINE
  "CASH RECEIVED / PAID",
]);

export default function CarExpenses({ sheet, month, onClose, onForceRebuild }) {
  const {
    baseSheet,
    incomeRows,
    expenseRows,
    setIncomeRows,
    setExpenseRows,
    loading,
    saving,
    error,
    save,
  } = useCarSheet(month, sheet);

  if (loading) return <div>Loading…</div>;
  if (!baseSheet) return <div>No sheet loaded</div>;

  const header = baseSheet;
  // --------------------------------------------------------
// 🔥 NEW — DELETE ONLY INCOME ROWS (NOT EXPENSES)
// --------------------------------------------------------
async function handleDeleteIncome() {
  if (
    !window.confirm(
      `Delete ALL INCOME for ${header.carNumber}?\n\nThis will NOT delete expenses.\n\nContinue?`
    )
  ) {
    return;
  }

  try {
    const ref = doc(db, "carSheets", month, "sheets", header.id);

    // Remove only income + cash
    await updateDoc(ref, {
      incomeRows: [],
      cashRows: [],
      updatedAt: new Date(),
    });

    alert(
      "Income deleted successfully.\nNow press BUILD SHEETS to reload fresh income."
    );

    // 🔥 Tell parent “Rebuild now”
    if (onForceRebuild) {
      onForceRebuild();
    }

  } catch (err) {
    console.error("❌ Failed to delete income:", err);
    alert("Failed to delete income: " + err.message);
  }
}


  // --------------------------------------------
  // HELPER FUNCTIONS (same as your old file)
  // --------------------------------------------
  const getIncomeLabel = (row) =>
    (row.company || row.label || "").toUpperCase().trim();

  const isRowEditable = (row) =>
    EDITABLE_INCOME_LABELS.has(getIncomeLabel(row)) &&
    row.rowType === "data";

  const recalcRowTotal = (row) => {
    const label = String(row?.label || "").toUpperCase().trim();

    // Fine Date row is informational (text), must NEVER be summed
    if (label.includes("FINE DATE")) return 0;

    let sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum += Number(row[`col${i}`] || 0);
    }
    return sum;
  };

  // --------------------------------------------
  // EXPENSE CHANGE (same as your old code)
  // --------------------------------------------
  const handleExpenseChange = (idx, key, value) => {
    setExpenseRows((prev) => {
      const next = [...prev];
      const row = { ...next[idx] };

      if (row.isSummary || row.isIncome) return prev;

      if (key.startsWith("col")) {
        const label = String(row?.label || "").toUpperCase().trim();

        // Fine Date row stores TEXT (e.g., "5 Dec") and should not affect totals
        if (label.includes("FINE DATE")) {
          row[key] = value; // keep as string
          row.total = 0;
        } else {
          row[key] = value === "" ? "" : Number(value);
          row.total = recalcRowTotal(row);
        }
      } else {
        row[key] = value;
        if (key === "total") row.total = Number(value || 0);
      }

      next[idx] = row;

      // Update TOTAL EXPENSES summary
      const summaryIndex = next.findIndex((r) => r.isSummary);
      if (summaryIndex !== -1) {
        next[summaryIndex].total = next
          .filter((r) => !r.isSummary && !r.isIncome)
          .reduce((acc, r) => acc + Number(r.total || 0), 0);
      }

      return next;
    });
  };

  // --------------------------------------------
  // INCOME CHANGE (old logic merged here)
  // --------------------------------------------
  const handleIncomeChange = (idx, key, val) => {
    setIncomeRows((prev) => {
      const next = [...prev];
      const row = { ...next[idx] };

      if (!isRowEditable(row)) return prev;

      if (key === "total") {
        row.total = val === "" ? "" : Number(val);
      } else {
        row[key] = val;
      }

      next[idx] = row;
      return next;
    });
  };

  // --------------------------------------------
// UI STARTS
// --------------------------------------------

return (
  <div style={{ padding: 12 }}>
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 12,
        padding: 10,
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        position: "sticky",
        top: 0,
        zIndex: 5,
      }}
    >
      <div>
        <h2 style={{ margin: 0 }}>
          Car Sheet — {header.carNumber} {header.carModel}
        </h2>

        <div style={{ fontSize: 13, marginTop: 4, color: "#555" }}>
          {header.monthLabel} | {header.periodLabel}
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        style={{
          padding: "10px 20px",
          background: saving ? "#ccc" : "#1976d2",
          color: "white",
          border: "none",
          borderRadius: 4,
          cursor: saving ? "not-allowed" : "pointer",
          fontWeight: "bold",
          whiteSpace: "nowrap",
        }}
      >
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>

    {error && (
      <div style={{ color: "red", marginBottom: 10 }}>{error}</div>
    )}

    {/* 🔥 NEW — DELETE INCOME ONLY BLOCK */}
    <div
      style={{
        marginBottom: 15,
        padding: 10,
        background: "#ffecec",
        border: "1px solid #ffcccc",
        borderRadius: 6,
      }}
    >
      <strong style={{ color: "#b30000" }}>Danger Zone:</strong>
      <br />
      <button
        onClick={handleDeleteIncome}
        style={{
          marginTop: 6,
          padding: "6px 14px",
          background: "#d9534f",
          color: "#fff",
          border: "none",
          borderRadius: 4,
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        Delete ALL Income for This Sheet
      </button>
      <div style={{ fontSize: 12, color: "#b30000", marginTop: 4 }}>
        This will delete incomeRows + cashRows only.  
        Expenses will remain safe.  
        After that → press BUILD SHEETS again to reload income.
      </div>
    </div>

    {/* Debug info */}
    <div
      style={{
        fontSize: 12,
        color: "#666",
        marginBottom: 10,
        padding: 8,
        background: "#f5f5f5",
        borderRadius: 4,
      }}
    >
      <strong>Editable income rows:</strong>{" "}
      {Array.from(EDITABLE_INCOME_LABELS).join(", ")}
    </div>

    {/* --------------------- INCOME TABLE ---------------------- */}
    <h3>INCOME</h3>

    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: 24,
        border: "1px solid #ddd",
      }}
    >
      <thead>
        <tr style={{ background: "#f0f0f0" }}>
          <th style={{ border: "1px solid #ddd", padding: 8 }}>
            Company / Label
          </th>
          <th style={{ border: "1px solid #ddd", padding: 8 }}>Driver</th>
          <th style={{ border: "1px solid #ddd", padding: 8 }}>W1</th>
          <th style={{ border: "1px solid #ddd", padding: 8 }}>W2</th>
          <th style={{ border: "1px solid #ddd", padding: 8 }}>W3</th>
          <th style={{ border: "1px solid #ddd", padding: 8 }}>W4</th>
          <th style={{ border: "1px solid #ddd", padding: 8 }}>W5</th>
          <th style={{ border: "1px solid #ddd", padding: 8 }}>Total</th>
        </tr>
      </thead>

      <tbody>
        {incomeRows.map((r, idx) => {
          const editable = isRowEditable(r);
          const isSummary = r.rowType === "summary";

          return (
            <tr
              key={idx}
              style={{
                background: editable
                  ? "#f0f8ff"
                  : isSummary
                  ? "#f9f9f9"
                  : "transparent",
              }}
            >
              <td style={{ border: "1px solid #ddd", padding: 8 }}>
                {r.company || r.label} {editable && " ✏️"}{" "}
                {isSummary && " 📊"}
              </td>

              <td style={{ border: "1px solid #ddd", padding: 8 }}>
                {r.driver || r.driverName || ""}
              </td>

              {[1, 2, 3, 4, 5].map((w) => (
                <td
                  key={w}
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    textAlign: "right",
                  }}
                >
                  {r[`week${w}`] || r[`week${w}`] === 0
                    ? Number(r[`week${w}`]).toFixed(2)
                    : ""}
                </td>
              ))}

              <td
                style={{
                  border: "1px solid #ddd",
                  padding: 8,
                  textAlign: "right",
                }}
              >
                {editable ? (
                  <input
                    type="number"
                    value={r.total ?? ""}
                    onChange={(e) =>
                      handleIncomeChange(idx, "total", e.target.value)
                    }
                    style={{
                      width: "100%",
                      textAlign: "right",
                      border: "2px solid #1976d2",
                      padding: 4,
                      borderRadius: 3,
                    }}
                  />
                ) : (
                  Number(r.total || 0).toFixed(2)
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>

    {/* -------------------- EXPENSE TABLE ----------------------- */}
<h3>EXPENSES</h3>

<table
  style={{
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #ddd",
  }}
>
  <thead>
    <tr style={{ background: "#f0f0f0" }}>
      <th style={{ border: "1px solid #ddd", padding: 8 }}>
        Expense Head
      </th>

      <th
        colSpan="10"
        style={{
          border: "1px solid #ddd",
          padding: 8,
          textAlign: "center",
        }}
      >
        Columns (Fines Only)
      </th>

      <th style={{ border: "1px solid #ddd", padding: 8 }}>Total</th>
    </tr>
  </thead>

  <tbody>
    {expenseRows.map((row, idx) => {
      if (row.isSummary) {
        return (
          <tr key={idx} style={{ background: "#e8f4fd" }}>
            <td style={{ border: "1px solid #ddd", padding: 8 }}>
              {row.label}
            </td>
            <td colSpan={10}></td>
            <td style={{ border: "1px solid #ddd", padding: 8 }}>
              {Number(row.total || 0).toFixed(2)}
            </td>
          </tr>
        );
      }

      const isFineDate = row.label === "FINE DATE";
      const isTrafficFines = row.label === "TRAFFIC FINES";

      if (isFineDate || isTrafficFines) {
        return (
          <tr key={idx}>
            <td style={{ border: "1px solid #ddd", padding: 8 }}>
              {row.label}
            </td>

            {[...Array(10)].map((_, i) => (
              <td key={i} style={{ border: "1px solid #ddd" }}>
                <input
                  // Use text input for FINE DATE, number for TRAFFIC FINES
                  type={isFineDate ? "text" : "number"}
                  value={row[`col${i + 1}`] || ""}
                  onChange={(e) =>
                    handleExpenseChange(idx, `col${i + 1}`, e.target.value)
                  }
                  style={{ 
                    width: "100%", 
                    textAlign: isFineDate ? "left" : "right",
                    // Optional: Different styling for text vs number
                    borderColor: isFineDate ? "#4CAF50" : "#2196F3"
                  }}
                  // Add placeholder for Fine Date
                  placeholder={isFineDate ? "Enter date" : ""}
                />
              </td>
            ))}

            <td style={{ border: "1px solid #ddd", padding: 8 }}>
              {/* Show total only for TRAFFIC FINES */}
              {isTrafficFines 
                ? Number(row.total || 0).toFixed(2)
                : "-" // or leave empty for Fine Date
              }
            </td>
          </tr>
        );
      }

      return (
        <tr key={idx}>
          <td style={{ border: "1px solid #ddd", padding: 8 }}>
            {row.label}
          </td>

          <td colSpan={10}>
            <input
              type="text"
              value={row.description || ""}
              onChange={(e) =>
                handleExpenseChange(idx, "description", e.target.value)
              }
              style={{ width: "100%" }}
            />
          </td>

          <td>
            <input
              type="number"
              value={row.total ?? ""}
              onChange={(e) =>
                handleExpenseChange(idx, "total", e.target.value)
              }
              style={{ width: "100%", textAlign: "right" }}
            />
          </td>
        </tr>
      );
    })}
  </tbody>
</table>

    
  </div>
);
}